
import { useQueryClient } from "@tanstack/react-query";
import { Profile } from "@/hooks/profile/types";
import { supabase } from "@/integrations/supabase/client";
import { validateProfileUpdate } from "../utils/ProfileValidationUtils";
import { useProfileValidation } from "./useProfileValidation";

interface UseProfileCompletionProps {
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  refetch: () => Promise<any>;
  onProfileComplete: () => void;
  onProfileError: () => void;
}

export const useProfileCompletion = ({
  updateProfile,
  refetch,
  onProfileComplete,
  onProfileError
}: UseProfileCompletionProps) => {
  const queryClient = useQueryClient();
  const { isProfileIncomplete } = useProfileValidation();

  // Función para completar el perfil con validación robusta
  const handleProfileComplete = async (updates: Partial<Profile>) => {
    try {
      // Validación exhaustiva antes de proceder
      validateProfileUpdate(updates);
      
      console.log("Updating profile with validated data:", updates);
      
      // Intentar actualizar el perfil
      await updateProfile(updates);
      
      // Forzar recarga de los datos del perfil desde Supabase
      await refetch();
      
      // Verificar que el perfil se actualizó correctamente
      let updatedProfile = queryClient.getQueryData(["profile"]) as Profile | undefined;
      console.log("Profile after update (from cache):", updatedProfile);

      // Fallback: si el cache aún se ve incompleto, releer directo de la DB
      if (!updatedProfile || isProfileIncomplete(updatedProfile)) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: freshProfile } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", user.id)
            .single();
          if (freshProfile) {
            updatedProfile = freshProfile as Profile;
            queryClient.setQueryData(["profile"], freshProfile);
            console.log("Profile after direct DB read:", freshProfile);
          }
        }
      }

      if (updatedProfile && !isProfileIncomplete(updatedProfile)) {
        console.log("Profile is now complete, cleaning up states");
        sessionStorage.setItem("profileCompleted", "true");
        onProfileComplete();
      } else {
        console.warn("Profile still incomplete after update:", updatedProfile);
        throw new Error("No se pudo completar la actualización del perfil. Por favor, intenta nuevamente.");
      }
    } catch (error) {
      console.error("Error al actualizar el perfil:", error);
      
      // Mantener el modal abierto en caso de error
      onProfileError();
      
      // Re-throw error para que sea manejado por el componente del modal
      throw error;
    }
  };

  return {
    handleProfileComplete
  };
};
